// Single source of truth for the claim's editable fields, grouped + labelled to
// match the WebForms detail page. Both ClaimView (read-only) and ClaimForm
// (edit) render from this, so labels/layout never drift apart.
//
// field: { name, label, type: "text"|"textarea"|"date"|"ref"|"select"|"check",
//          source?, options?, wide?, gated?, visibleWhen? }
//   ref source is a System Data entity key, or "users".
//   select carries its options list (value === label).
//   visibleWhen(ctx) hides state-dependent fields; ctx = { jobTypeName,
//   statusCode } lower-cased, plus wasAccepted and `values` (the claim being
//   viewed or the form being edited, for fields gated on another FIELD rather
//   than on status) — provided by ClaimForm/ClaimView.
//   wide fields span the full row.
//   mailto marks a field the READ view renders as an email link: "value" = the
//   field holds the address (client email); "ref" = the referenced contact's
//   address and CC list ride along on the claim (assessor + routing).
// section: { title, fields, startClosed?, editOnly? }
//   EVERY section is a <details> and opens by default. `startClosed` is the one
//   that arrives shut (assessor routing — quiet by default, as it always was).
//   Collapsing is not decoration: it is how the office controls what a PRINTED
//   job sheet contains, because a shut section does not print at all
//   (styles/print.css). Robert, 13 Aug 2026.
//   editOnly sections are skipped by ClaimView (flags render as a banner there).

// The live data's exact PropertyAge vocabulary (95% fill over the last year —
// see docs/business-rules/details.md 3.1).
export const PROPERTY_AGE_OPTIONS = [
  "pre-1920", "1920-29", "1930-39", "1940-49", "1950-59", "1960-69",
  "1970-79", "1980-89", "1990-99", "2000-09", "2010-19", "2020-29",
  "N/A", "mixed/remod",
];

const isTender = (ctx) => ctx.jobTypeName === "tender reinstatement";
const isCallout = (ctx) => ctx.jobTypeName === "callout emergency works";
// "Accepted" for the purpose of showing a field means HAS BEEN accepted, so a
// job re-opened by a new inspection keeps its scheduling dates on screen.
const isAccepted = (ctx) => ctx.statusCode === "A" || ctx.wasAccepted === true;

export const CLAIM_SECTIONS = [
  {
    title: "Job", // the tab's TabHeader is "Claim Details" — don't double up
    fields: [
      { name: "division_id", label: "Division", type: "ref", source: "divisions", gated: true },
      { name: "insurer_company_id", label: "Insurer/Debtor", type: "ref", source: "insurer-companies" },
      { name: "weather_event_id", label: "Weather Event", type: "ref", source: "weather-events" },
      // required: mandatory on save (legacy rule) — server rejects create/clear without it.
      { name: "job_type_id", label: "Job Type", type: "ref", source: "job-types", gated: true, required: true },
      { name: "job_category_id", label: "Job Category", type: "ref", source: "job-categories" },
      { name: "insurance_claim_number", label: "Insurance Claim#", type: "text", narrow: true },
      { name: "property_excess", label: "Property Excess", type: "text", gated: true, narrow: true },
      { name: "property_age", label: "Property Age", type: "select", options: PROPERTY_AGE_OPTIONS },
      { name: "tender_due_date", label: "Tender Due Date", type: "date", visibleWhen: isTender },
    ],
  },
  {
    title: "Flags",
    editOnly: true, // ClaimView shows these as the red banner instead
    fields: [
      { name: "is_important_job", label: "Important Job", type: "check" },
      { name: "is_problem_job", label: "Problem Job", type: "check" },
      { name: "is_contractor_quoting", label: "Contractor Quoting", type: "check" },
      {
        name: "qr_status",
        label: "Quote Request (Callout)",
        type: "select",
        options: [
          { value: "received", label: "QR Received" },
          { value: "not_required", label: "QR Not Required" },
        ],
        visibleWhen: isCallout,
      },
    ],
  },
  {
    title: "Assessor",
    fields: [
      { name: "assessor_company_id", label: "Assessor Co", type: "ref", source: "assessor-companies" },
      // `phone: "ref"` — the read view shows the assessor's phone on a second
      // line under the name (KR + KS 2026-09-10: view only, never an edit).
      { name: "assessor_id", label: "Assessor", type: "ref", source: "assessors", mailto: "ref", phone: "ref" },
      { name: "assessor_ref", label: "Assessor Ref#", type: "text", narrow: true },
    ],
  },
  {
    // Legacy AssessorQuotesTo/UpdatesTo/VariationsTo/InvoicesTo — auto-defaulted
    // to the assessor 97% of the time, so it lives folded away.
    // Each reads as an email link (1.0's header did the same), addressed to that
    // contact and copying whoever their CC field names — Karen Renall 2026-08-05
    // #11, "click the Invoices To details to open an email, cc Accounts and
    // Support", which the five "... INVOICES" contacts already say to do.
    title: "Assessor Routing",
    startClosed: true, // the only one that arrives shut
    fields: [
      { name: "assessor_quotes_to_id", label: "Quotes To", type: "ref", source: "assessors", mailto: "ref" },
      { name: "assessor_updates_to_id", label: "Updates To", type: "ref", source: "assessors", mailto: "ref" },
      { name: "assessor_variations_to_id", label: "Variations To", type: "ref", source: "assessors", mailto: "ref" },
      { name: "assessor_invoices_to_id", label: "Invoices To", type: "ref", source: "assessors", mailto: "ref" },
    ],
  },
  {
    title: "People & Dates",
    fields: [
      { name: "project_coordinator_id", label: "Project Co-Ordinator", type: "ref", source: "users" },
      { name: "inspector_id", label: "Inspector", type: "ref", source: "inspectors" },
      // Karen Sweatman, 2026-08-12: "is the initial Go Ahead Date the same as
      // initial contact?" — the two names could not tell you apart, so the
      // LABELS now say what each one records and the hints say when it is
      // stamped. Neither field's meaning changed: first_contact_date still
      // drives the Log→Contact SLA and File Life, and both are still stamped
      // once by the first matching Client Update (services/progress.js).
      //
      // The date the job was ACCEPTED — what she calls the "Go Ahead Date" — is
      // deliberately NOT a field here: it lives in the status history, where it
      // cannot drift from the status it describes.
      // HIDDEN "for now" (Robert, 13 Aug meeting). The fields stay declared, and
      // stay in the form payload, so the values keep round-tripping untouched —
      // this removes them from the SCREEN, not from the record. Both are still
      // stamped automatically by the first matching Client Update, both still
      // show on the Client Contact panel, and first_contact_date still drives
      // the Log-to-Contact SLA and File Life. Delete `hidden` to bring them back.
      {
        name: "first_contact_date",
        label: "First Client Contact",
        type: "date",
        hidden: true,
        hint:
          "The first Client Update recorded on this job, whatever its status. " +
          "Stamped once, automatically, by that entry — and it drives the " +
          "Log-to-Contact SLA, so changing it changes File Life.",
      },
      {
        name: "initial_go_ahead_date",
        label: "Go Ahead Contact Date",
        type: "date",
        hidden: true,
        hint:
          "When the client was told the job is going ahead: the first Client " +
          "Update recorded once the job was Accepted. Stamped once, " +
          "automatically. The date the job was ACCEPTED is on Status & History.",
      },
      // The four scheduling dates appear once the job has been ACCEPTED and not
      // before (Karen Sweatman: "neither date, nor estimated/actual start/end,
      // to show unless accepted" — Robert's "bottom two lines hide if not
      // accepted", 13 Aug). Nothing is meant to set them earlier: the two
      // estimates are AUTO-FILLED on accept for Standard R&Q / Standard Go Ahead
      // (details.md §5), and an empty pair of date boxes on a job nobody has
      // authorised is an invitation to type in one.
      //
      // `wasAccepted`, NOT statusCode === "A". A job re-opened by a new
      // inspection sits at R or I with its work authorised and often underway;
      // gating on the current status would blank four live dates the day
      // somebody books a re-inspection. Same trap as the PCO Dashboard's
      // overdue lines (rdr-reports.md).
      { name: "estimated_start_date", label: "Estimated Start Date", type: "date", visibleWhen: isAccepted },
      { name: "actual_start_date", label: "Actual Start Date", type: "date", visibleWhen: isAccepted },
      { name: "estimated_end_date", label: "Estimated End Date", type: "date", visibleWhen: isAccepted },
      { name: "actual_end_date", label: "Actual End Date", type: "date", visibleWhen: isAccepted },
    ],
  },
  {
    title: "Client",
    fields: [
      { name: "client_name", label: "Client Name", type: "text" },
      { name: "contact_name", label: "Contact", type: "text" },
      // `client: true` — the homeowner, so the mailto carries 1.0's client
      // subject ("GPS 76468 - INSURANCE WORKS AT …") rather than the
      // reference-laden line the trade gets. See utils/claimEmail.js.
      { name: "email_address", label: "Email Address", type: "text", mailto: "value", client: true },
      { name: "business_phone", label: "Business Ph", type: "text", narrow: true },
      { name: "mobile_phone", label: "Mobile Ph", type: "text", narrow: true },
      { name: "street_address", label: "Street Address", type: "textarea", wide: true },
    ],
  },
  {
    title: "Notes",
    fields: [
      { name: "lockbox_location", label: "Lockbox Location", type: "text" },
      // Collecting the lockbox is its own fact. Before this existed the only way
      // to clear the "Lockbox To Be Picked Up" badge was to blank the location
      // above, which destroyed the record of where the lockbox had been (Karen
      // Sweatman, 13 Aug). Shown only once there is a location to collect.
      {
        name: "lockbox_picked_up",
        label: "Lockbox",
        type: "radio",
        onLabel: "Picked up",
        offLabel: "Still to be picked up",
        visibleWhen: (ctx) => String(ctx.values?.lockbox_location || "").trim() !== "",
      },
      { name: "rdr_note", label: "RDR Note", type: "textarea", wide: true },
      // Legacy GPSLog.LogSummary (1.0's Progress quick box) — home is HERE;
      // also editable from any tab via the glance quick facts.
      { name: "job_summary", label: "Job Summary", type: "textarea", wide: true },
      { name: "other_info", label: "Other Info", type: "textarea", wide: true },
      { name: "notes", label: "Notes", type: "textarea", wide: true },
    ],
  },
  // Karen Renall 2026-08-05: "The Work/Quote Request area is very important and
  // needs to be separate from everything else." It is what the insurer actually
  // asked for, so it gets its own heading rather than sitting fifth in a list of
  // notes. Same field, same home, just not buried.
  {
    title: "Work / Quote Request",
    fields: [
      { name: "work_request_details", label: "Work / Quote Request Details", type: "textarea", wide: true },
    ],
  },
];

export const CLAIM_FIELDS = CLAIM_SECTIONS.flatMap((s) => s.fields);

// Ref sources that name PEOPLE rather than a System Data entity: they come from
// /user/users (labelled `full_name`), not from /system-data/<key> (`name`).
//
// This list is why the file owns it rather than ClaimForm: everything below and
// everything in ClaimForm keys off the SAME set. When "inspectors" was added as
// a second people source on 2026-08-13 the exclusion here still read
// `!== "users"`, so "inspectors" was taken for a lookup entity, the
// /system-data/inspectors fetch 404'd, and the Promise.all that loads EVERY
// dropdown rejected — New Claim showed "Unable to load dropdown data." with an
// empty Job Type, which is a required field, so no claim could be created at
// all. A third people source must not be able to do that again.
export const PEOPLE_SOURCES = new Set(["users", "inspectors"]);

// Lookup entity keys the form needs (ref sources that are System Data entities).
export const CLAIM_LOOKUP_SOURCES = [
  ...new Set(
    CLAIM_FIELDS.filter((f) => f.type === "ref" && !PEOPLE_SOURCES.has(f.source)).map(
      (f) => f.source
    )
  ),
];

export function emptyClaimForm() {
  return Object.fromEntries(CLAIM_FIELDS.map((f) => [f.name, ""]));
}

// X-Ref copy rules (legacy DetailsEdit.CrossRefToPage): the new claim inherits
// the job's identity, but NOT its dates ("don't want any of these copying
// over"), callout QR state, or free notes — those belong to the source job.
const XREF_EXCLUDED = new Set([
  "first_contact_date", "initial_go_ahead_date",
  "estimated_start_date", "actual_start_date",
  "estimated_end_date", "actual_end_date",
  "qr_status", "notes", "job_summary",
]);

export function xrefClaimForm(source) {
  const form = emptyClaimForm();
  for (const f of CLAIM_FIELDS) {
    if (XREF_EXCLUDED.has(f.name)) continue;
    const v = source[f.name];
    if (v === undefined || v === null) continue;
    form[f.name] = f.type === "check" ? !!v : String(v);
  }
  return form;
}

// For a ref field, the claim's joined display value (e.g. division_id -> division_name).
export function refDisplayKey(name) {
  return name.replace(/_id$/, "_name");
}

// The joined contact details a mailto: "ref" field emails
// (assessor_invoices_to_id -> assessor_invoices_to_email / _cc).
export function refEmailKey(name) {
  return name.replace(/_id$/, "_email");
}
export function refCcKey(name) {
  return name.replace(/_id$/, "_cc");
}
export function refPhoneKey(name) {
  return name.replace(/_id$/, "_phone");
}

// The visibility context both renderers share. From the claim (view) or the
// form + job-type options (edit) — always lower-cased names.
export function fieldContext({
  claim = null,
  form = null,
  jobTypes = [],
  statusCode = "",
  wasAccepted = false,
} = {}) {
  if (claim) {
    return {
      jobTypeName: String(claim.job_type_name || "").trim().toLowerCase(),
      statusCode: claim.current_status_code || "",
      wasAccepted: claim.ever_accepted === true,
      // The record itself, for the handful of fields whose visibility depends on
      // another FIELD rather than on job type or status.
      values: claim,
    };
  }
  // The EDIT form has no claim — it renders from `form`, which carries only the
  // editable fields and so knows nothing about status. The two facts that gate
  // visibility are therefore passed in: the Details tab has the claim and hands
  // them over, New Claim leaves them at the defaults and correctly shows the
  // pre-accept form.
  const jt = jobTypes.find((t) => String(t.id) === String(form?.job_type_id));
  return {
    jobTypeName: String(jt?.name || "").trim().toLowerCase(),
    statusCode,
    wasAccepted,
    values: form || {},
  };
}

/** The fields of a section that should render, for this context. Shared so the
 *  read view and the edit form can never disagree about what is on screen. */
export function visibleFields(section, ctx) {
  return section.fields.filter((f) => !f.hidden && (!f.visibleWhen || f.visibleWhen(ctx)));
}

// Sensitive fields only Administrators/PowerUsers may change (mirrors the server
// rule in services/claims.js — the ";"-separated groups drive permissions).
export const GATED_CLAIM_FIELDS = CLAIM_SECTIONS.flatMap((s) =>
  s.fields.filter((f) => f.gated).map((f) => f.name)
);
export function canEditGatedFields(user) {
  if (!user) return false;
  const groups = String(user.member_of || "").split(";").map((g) => g.trim().toLowerCase());
  return !!user.is_admin || groups.includes("poweruser");
}
